import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { CurrencyBar } from "../../common_ui_elements/currency_bar.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { goalIconFor } from "../goal_icon.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";
import { COIN_TRANSITION_BADGE_FADE_MS } from "../coin_transition_badge.js";
import { Vfx2DManager } from "../../../webgl_2d/vfx_2d_manager.js";
import { WebGLUIRuntime } from "../../../webgl_2d/webgl_ui_runtime.js";
import { Vfx2DRuntime } from "../../../webgl_2d/vfx_2d_runtime.js";
import { FireworksColor } from "../../../webgl_2d/vfx/fireworks/fireworks_types.js";
import { MoveDirection } from "../../../common/types.js";
import { Game } from "../../../game.js";
import { tryShowInterstitial } from "../../../ads/interstitial.js";
import { PlayDialogDifficulty } from "../../dialog/play_dialog.js";
import { getLevelDifficulty } from "../../../level_config.js";
import { LevelPackService } from "../../../level_pack/level_pack_service.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { CoinIconEffectController } from "../../../webgl_2d/vfx/coins/coin_icon_effect_controller.js";
import { IController } from "../../../webgl_2d/core/types/controller_types.js";
import { hapticsController, VibrationContext } from "../../../haptics/haptics_controller.js";
import { HapticPattern } from "../../../haptics/haptics_constants.js";
import { AdventureRewardType, ADVENTURE_REWARD_ICONS } from "../../../adventure/adventure_box_rewards.js";
import { isTimedRewardType, rewardAmountText } from "../../../common/reward_format.js";
import { pulseCannons } from "../../common_ui_elements/cannon_pulse.js";

export type ResultRewardCurrency = AdventureRewardType;

export interface ResultReward
{
  currency: ResultRewardCurrency;
  amount: number;
}

export interface GameResultOverlayParams
{
  level?: number;
  difficulty?: PlayDialogDifficulty;
  title_key?: string;
  stars?: number;
  rewards?: ResultReward[];
  reward_title_key?: string;
  rewarded_level?: number;
  onContinue?: () => void;
  // Returns the actual coin bonus granted by the server, so the popup can
  // display the real doubled total instead of assuming amount * 2.

}

const DESIGN_W = 1080;
const DESIGN_H = 1920;
const DESIGN_CX = DESIGN_W / 2;
const DESIGN_CY = DESIGN_H / 2;

const RESULT_SHADOW_COLOR = "rgba(16, 6, 84, 0.43)";

interface Box { x: number; y: number; w: number; h: number; cx: number; }

export class GameResultOverlay extends AbstractOverlay
{
  private static readonly APPEAR_MS = 240;
  private static readonly COIN_BADGE_POP_MS = 160;
  private static readonly COIN_BADGE_SETTLE_MS = 100;
  private static readonly COIN_FLY_DELAY_MS = 180;

  // How long the win overlay stays up after Claim. The coins leave the reward
  // icon at COIN_FLY_DELAY_MS, so this has to be comfortably longer than that
  // or the overlay is gone before the flight reads as having started here —
  // at 320ms only ~140ms of it was ever seen on this screen. The rest of the
  // flight finishes over whatever comes next (the loading screen, or the menu).
  private static readonly COIN_OVERLAY_HOLD_MS = 600;
  private static readonly RESULT_TEXT_ARC_RADIUS_DESIGN = 2200;

  private static readonly BANNER_W_DESIGN = 1059;
  private static readonly BANNER_H_DESIGN = 465;
  private static readonly BANNER_Y_DESIGN = 210;
  private static readonly BANNER_RIBBON_Y_DESIGN = 136;
  private static readonly BANNER_RIBBON_H_DESIGN = 329;
  private static readonly BANNER_CANNONS_X_DESIGN = 71;
  private static readonly BANNER_CANNONS_Y_DESIGN = 0;
  private static readonly BANNER_CANNONS_W_DESIGN = 917;
  private static readonly BANNER_CANNONS_H_DESIGN = 246;

  private static readonly REWARD_ICON_W_DESIGN = 182;
  private static readonly REWARD_ICON_H_DESIGN = 182;
  private static readonly REWARD_ICON_CY_DESIGN = 900;
  private static readonly REWARD_GAP_X_DESIGN = 120;

  private static readonly BUTTON_Y_DESIGN = 1170;

  private static readonly GOAL_PANEL_X_DESIGN = 40;
  private static readonly GOAL_PANEL_W_DESIGN = 1000;
  private static readonly GOAL_PANEL_Y_DESIGN = 1555;
  private static readonly GOAL_PANEL_H_DESIGN = 275;
  private static readonly GOAL_HEADER_W_DESIGN = 806;
  private static readonly GOAL_HEADER_H_DESIGN = 125;

  private static readonly FIREWORKS_COLORS: ReadonlyArray<FireworksColor> = [
    FireworksColor.Orange,
    FireworksColor.Red,
    FireworksColor.Green,
    FireworksColor.Blue,
    FireworksColor.Purple,
  ];

  private static readonly FIREWORKS_SERIES_COUNT     : number = 3;
  private static readonly FIREWORKS_SHOTS_PER_SERIES : number = 4;
  private static readonly FIREWORKS_SERIES_DELAY     : number = 800;
  private static readonly FIREWORKS_BASE_Y_RATIO     : number = 0.7;
  private static readonly FIREWORKS_OFFSET_X_MIN     : number = -300;
  private static readonly FIREWORKS_OFFSET_X_RANGE   : number = 600;
  private static readonly FIREWORKS_OFFSET_Y_RANGE   : number = 200;
  private static readonly FIREWORKS_HEIGHT_MIN       : number = 400;
  private static readonly FIREWORKS_HEIGHT_RANGE     : number = 750;
  private static readonly FIREWORKS_DRIFT_X_MIN      : number = -50;
  private static readonly FIREWORKS_DRIFT_X_RANGE    : number = 100;
  private static readonly FIREWORKS_RANDOM_DELAY_MAX : number = 400;
  private static readonly FIREWORKS_EXPLOSION_DELAY  : number = 700;

  private interactive_at = 0;
  private continued = false;
  private reward_inflight = false;
  private reward_elements: UIElement[] = [];
  private reward_amount_texts: UITextElement[] = [];
  private coin_badge?: UIElement;
  private coin_badge_target = new Vector2( 0, 0 );
  private coin_icon_controller: CoinIconEffectController | null = null;
  private reward_glow_controller: IController | null = null;
  private is_closed: boolean = false;
  private reward_finish_at = 0;
  private keep_coin_flight_on_close = false;
  private fireworks_timeout_ids: number[] = [];
  private fireworks_stopped = true;

  /**
   * Did finishing this level earn the next block? The block appears AT its
   * unlock level, so it is earned by clearing the one before it — which is
   * exactly when the bar reads full.
   *
   * No schedule means no block to celebrate: the congratulation is silent
   * rather than generic.
   */
  private static goalJustEarned( completed_level: number ): boolean
  {
    return LevelPackService.isScheduledLevel( completed_level + 1 );
  }

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: GameResultOverlayParams,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );

    this.createBanner();
    this.createResultText();
    this.createReward();
    this.createGoalProgress();
    this.createRewardButtons();
    this.startAppearAnimation();

    this.interactive_at = performance.now() + GameResultOverlay.APPEAR_MS * 0.6;

    if ( params?.stars && params.stars >= 1 )
    {
      const difficulty = params.difficulty ?? ( params.level !== undefined ? getLevelDifficulty( params.level ) : PlayDialogDifficulty.Common );
      if ( difficulty === PlayDialogDifficulty.Common )
      {
        Vfx2DManager.addConfettiBottom( performance.now() );
      }
      else
      {
        this.addFireworks();
        Vfx2DManager.addConfettiSide( performance.now() );
      }
    }
  }

  private addFireworks(): void
  {
    this.fireworks_stopped = false;
    const screen_w = Game.getScreenWidth();
    const screen_h = Game.getScreenHeight();
    const base = new Vector2( screen_w / 2, screen_h * GameResultOverlay.FIREWORKS_BASE_Y_RATIO );

    for ( let s = 0; s < GameResultOverlay.FIREWORKS_SERIES_COUNT; s++ )
    {
      const series_ms = s * GameResultOverlay.FIREWORKS_SERIES_DELAY;
      for ( let i = 0; i < GameResultOverlay.FIREWORKS_SHOTS_PER_SERIES; i++ )
      {
        const offset_x = LayoutScaler.widthFromDesign( GameResultOverlay.FIREWORKS_OFFSET_X_MIN + Math.random() * GameResultOverlay.FIREWORKS_OFFSET_X_RANGE );
        const offset_y = LayoutScaler.widthFromDesign( Math.random() * GameResultOverlay.FIREWORKS_OFFSET_Y_RANGE );
        const height = LayoutScaler.widthFromDesign( GameResultOverlay.FIREWORKS_HEIGHT_MIN + Math.random() * GameResultOverlay.FIREWORKS_HEIGHT_RANGE );

        const color = GameResultOverlay.FIREWORKS_COLORS[Math.floor( Math.random() * GameResultOverlay.FIREWORKS_COLORS.length )];
        const dir = Math.random() > 0.5 ? MoveDirection.Forward : MoveDirection.Backward;
        const delay = series_ms + Math.random() * GameResultOverlay.FIREWORKS_RANDOM_DELAY_MAX;

        const timeout_id = window.setTimeout( () =>
        {
          if ( this.fireworks_stopped )
            return;

          const start = new Vector2( base.x + offset_x, base.y + offset_y );
          const end = new Vector2(
            start.x + LayoutScaler.widthFromDesign( GameResultOverlay.FIREWORKS_DRIFT_X_MIN + Math.random() * GameResultOverlay.FIREWORKS_DRIFT_X_RANGE ),
            start.y - height
          );
          const now = performance.now();
          Vfx2DManager.addFireworksTrail( start, end, color, dir, now );
          Vfx2DManager.addFireworksExplosion( end, color, now + GameResultOverlay.FIREWORKS_EXPLOSION_DELAY );
        }, delay );
        this.fireworks_timeout_ids.push( timeout_id );
      }
    }
  }

  private stopFireworks(): void
  {
    if ( this.fireworks_stopped )
      return;

    this.fireworks_stopped = true;
    for ( const timeout_id of this.fireworks_timeout_ids )
      window.clearTimeout( timeout_id );
    this.fireworks_timeout_ids.length = 0;
    Vfx2DManager.clearFireworks();
  }

  private box( dx: number, dy: number, dw: number, dh: number ): Box
  {
    const w = LayoutScaler.widthFromDesign( dw );
    const h = LayoutScaler.heightFromDesign( dh );
    const cx = this.rect.getW2() + LayoutScaler.widthFromDesign( ( dx + dw / 2 ) - DESIGN_CX );
    const cy = this.rect.getH2() + LayoutScaler.heightFromDesign( ( dy + dh / 2 ) - DESIGN_CY );
    return { x: cx - w / 2, y: cy - h / 2, w, h, cx };
  }

  private createBanner(): void
  {
    const params = this.params as GameResultOverlayParams;
    const banner_x_design = ( DESIGN_W - GameResultOverlay.BANNER_W_DESIGN ) / 2;

    const cannons_x_design = banner_x_design + GameResultOverlay.BANNER_CANNONS_X_DESIGN;
    const cannons_y_design = GameResultOverlay.BANNER_Y_DESIGN + GameResultOverlay.BANNER_CANNONS_Y_DESIGN;
    const cannons_b = this.box(
      cannons_x_design,
      cannons_y_design,
      GameResultOverlay.BANNER_CANNONS_W_DESIGN,
      GameResultOverlay.BANNER_CANNONS_H_DESIGN
    );
    const cannons = new UIImage( this.ctx, cannons_b.x, cannons_b.y, cannons_b.w, cannons_b.h, ImgID.UI_ResultBannerCannons );
    this.addChild( cannons );

    const ribbon_y_design = GameResultOverlay.BANNER_Y_DESIGN + GameResultOverlay.BANNER_RIBBON_Y_DESIGN;
    const ribbon_b = this.box(
      banner_x_design,
      ribbon_y_design,
      GameResultOverlay.BANNER_W_DESIGN,
      GameResultOverlay.BANNER_RIBBON_H_DESIGN
    );
    const ribbon = new UIImage( this.ctx, ribbon_b.x, ribbon_b.y, ribbon_b.w, ribbon_b.h, ImgID.UI_ResultBannerRibbon );
    this.addChild( ribbon );

    if ( params.stars && params.stars >= 1 )
      pulseCannons( cannons );
  }

  private resultShadow(): { shadow_blur: number; shadow_color: string; shadow_offset_x: number; shadow_offset_y: number }
  {
    return {
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_color: RESULT_SHADOW_COLOR,
      shadow_offset_x: LayoutScaler.xFromDesign( 0 ),
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
    };
  }

  private createResultText(): void
  {
    const params = this.params as GameResultOverlayParams;
    const b = this.box( 160, GameResultOverlay.BANNER_Y_DESIGN + 205, 760, 117 );

    const text = new UITextElement(
      this.ctx, b.x, b.y, b.w, b.h,
      {
        localization_key: params?.title_key ?? "RESULT.LEVEL_PASSED",
        font_size: LayoutScaler.textSizeFromDesign( 72 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 12 ),
        outline_color: "#05267E",
        auto_wrap: false,
        arc_radius: LayoutScaler.widthFromDesign( GameResultOverlay.RESULT_TEXT_ARC_RADIUS_DESIGN ),
        letter_spacing: LayoutScaler.widthFromDesign( 4 ),
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 44 ),
        ...this.resultShadow(),
      }
    );
    this.addChild( text );
  }

  public setRewards( rewards: ResultReward[] ): void
  {
    if ( this.is_closed ) return;

    for ( const element of this.reward_elements )
      this.removeChild( element );

    this.reward_elements = [];
    this.reward_amount_texts = [];

    this.reward_glow_controller?.stop();
    this.reward_glow_controller = null;

    ( this.params as GameResultOverlayParams ).rewards = rewards;

    this.createReward();
    this.invalidateSubtree();
  }

  private createReward(): void
  {
    if ( this.is_closed ) return;

    const params = this.params as GameResultOverlayParams;
    const rewards = params?.rewards ?? [];
    if ( rewards.length === 0 ) return;

    this.createLevelRewards( rewards );
  }

  private levelRewardCenterX( index: number, count: number ): number
  {
    const step = GameResultOverlay.REWARD_ICON_W_DESIGN + GameResultOverlay.REWARD_GAP_X_DESIGN;
    const row_w = count * GameResultOverlay.REWARD_ICON_W_DESIGN + ( count - 1 ) * GameResultOverlay.REWARD_GAP_X_DESIGN;
    const first_cx = DESIGN_CX - row_w / 2 + GameResultOverlay.REWARD_ICON_W_DESIGN / 2;

    return first_cx + index * step;
  }

  private createLevelRewards( rewards: ResultReward[] ): void
  {
    const overlay_absolute = this.getAbsolutePosition();
    const icon_w_design = GameResultOverlay.REWARD_ICON_W_DESIGN;
    const icon_h_design = GameResultOverlay.REWARD_ICON_H_DESIGN;
    const icon_y_design = GameResultOverlay.REWARD_ICON_CY_DESIGN - icon_h_design / 2;
    const amount_w_design = 260;
    const amount_h_design = 70;
    const amount_y_design = icon_y_design + icon_h_design - 37;

    rewards.forEach( ( reward, i ) =>
    {
      const is_coin = reward.currency === "coin";
      const center_dx = this.levelRewardCenterX( i, rewards.length );
      const icon_b = this.box( center_dx - icon_w_design / 2, icon_y_design, icon_w_design, icon_h_design );
      const icon_id = is_coin ? ImgID.UI_ShopBlockImg : GameResultOverlay.currencyImage( reward.currency );

      if ( is_coin )
      {
        const glow_center = new Vector2( overlay_absolute.x + icon_b.cx, overlay_absolute.y + icon_b.y + icon_b.h / 2 );
        const glow_radius = LayoutScaler.widthFromDesign( 115 );

        this.reward_glow_controller = Vfx2DManager.addCoinCounterGlow(
          glow_center,
          glow_radius,
          performance.now(),
          0,
          true,
          GameResultOverlay.APPEAR_MS,
          true,
          0.45
        );
      }

      const icon = new UIImage( this.ctx, icon_b.x, icon_b.y, icon_b.w, icon_b.h, icon_id );
      this.addChild( icon );
      this.reward_elements.push( icon );

      const amount_b = this.box( center_dx - amount_w_design / 2, amount_y_design, amount_w_design, amount_h_design );
      const amount_text = isTimedRewardType( reward.currency ) ? rewardAmountText( reward.currency, reward.amount ) : `${reward.amount}`;

      const amount = new UITextElement(
        this.ctx,
        amount_b.x,
        amount_b.y,
        amount_b.w,
        amount_b.h,
        {
          text: amount_text,
          font_size: LayoutScaler.textSizeFromDesign( 55 ),
          font_weight: 900,
          text_color: ColorScheme.white,
          text_style: TextStyleScheme.rubik,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
          outline_color: ColorScheme.friends_title_outline,
          auto_wrap: false,
          ...this.resultShadow(),
        }
      );
      this.addChild( amount );
      this.reward_elements.push( amount );

      if ( is_coin )
        this.reward_amount_texts.push( amount );
    } );
  }

  private createCoinBadge(): void
  {
    if ( this.coin_badge ) return;

    // Laid out exactly like the menu's coin block, which it stands in for.
    const bg_w = LayoutScaler.widthFromDesign( CurrencyBar.BLOCK_W );
    const bg_h = LayoutScaler.heightFromDesign( CurrencyBar.BLOCK_H );
    const bg_x = CurrencyBar.defaultCoinBlockX();
    const bg_y = LayoutScaler.yFromDesign( CurrencyBar.BLOCK_Y );

    const bg = new UIImage(
      this.ctx,
      bg_x,
      bg_y,
      bg_w,
      bg_h,
      ImgID.UI_MenuBlockBg
    );
    bg.setClickable( false );
    this.addChild( bg );
    this.coin_badge = bg;

    const icon_w = LayoutScaler.heightFromDesign( CurrencyBar.COIN_ICON_SIZE );
    const icon_h = icon_w;
    const icon_x = LayoutScaler.xFromDesign( CurrencyBar.ICON_X );
    const icon_y = bg_h / 2 - icon_h / 2;

    const bg_absolute = bg.getAbsolutePosition();
    this.coin_badge_target.set(
      bg_absolute.x + icon_x + icon_w / 2,
      bg_absolute.y + icon_y + icon_h / 2
    );

    const design_count_y = 0;

    const count_w = LayoutScaler.widthFromDesign( CurrencyBar.COUNT_W );
    const count_h = bg_h;
    const count_right_pad = LayoutScaler.widthFromDesign( CurrencyBar.COUNT_RIGHT_PAD );

    const count_x = bg_w - count_w - count_right_pad;
    const count_y = -LayoutScaler.yFromDesign( design_count_y );

    const count_text = new UITextElement(
      this.ctx,
      count_x,
      count_y,
      count_w,
      count_h,
      {
        text: StatisticsStorage.load().coins.toString(),
        font_size: LayoutScaler.textSizeFromDesign( CurrencyBar.COUNT_FONT ),
        font_weight: 700,
        text_color: ColorScheme.menu_top_el_text,
        text_style: TextStyleScheme.rubik,
      }
    );
    bg.addChild( count_text );
  }

  private createRewardButtons(): void
  {
    const b = this.box( ( DESIGN_W - 700 ) / 2, GameResultOverlay.BUTTON_Y_DESIGN, 700, 280 );
    const button = new UIImage( this.ctx, b.x, b.y, b.w, b.h, ImgID.UI_GreenFrameBtn, undefined, true );

    button.addChild( new UITextElement(
      this.ctx, 0, LayoutScaler.heightFromDesign( 6 ), b.w, b.h,
      {
        localization_key: "GET",
        font_size: LayoutScaler.textSizeFromDesign( 80 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.menu_button_outline,
        ...this.resultShadow(),
      }
    ), true );

    button.setOnClick( () => void this.collectGetReward( button ) );
    this.addChild( button );
  }

  private createGoalProgress(): void
  {
    const params = this.params as GameResultOverlayParams;
    if ( ( params.stars ?? 0 ) <= 0 )
      return;

    const completed_level = Math.max(
      1,
      Math.floor( params.rewarded_level ?? params.level ?? StatisticsStorage.load().level )
    );
    const span = LevelPackService.getFeatureProgress( completed_level );
    if ( !span )
      return;

    const target = span.target;
    const progress = span.progress;
    const is_available = progress >= target;

    const panel_b = this.box(
      GameResultOverlay.GOAL_PANEL_X_DESIGN,
      GameResultOverlay.GOAL_PANEL_Y_DESIGN,
      GameResultOverlay.GOAL_PANEL_W_DESIGN,
      GameResultOverlay.GOAL_PANEL_H_DESIGN
    );
    const panel = new UINineSliceElement(
      this.ctx,
      panel_b.x,
      panel_b.y,
      panel_b.w,
      panel_b.h,
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );
    this.addChild( panel );

    const header_overlap_design = 43;
    const header_x_design = ( DESIGN_W - GameResultOverlay.GOAL_HEADER_W_DESIGN ) / 2;
    const header_y_design = GameResultOverlay.GOAL_PANEL_Y_DESIGN - GameResultOverlay.GOAL_HEADER_H_DESIGN + header_overlap_design;
    const header_b = this.box( header_x_design, header_y_design, GameResultOverlay.GOAL_HEADER_W_DESIGN, GameResultOverlay.GOAL_HEADER_H_DESIGN );

    const header = new UIImage( this.ctx, header_b.x, header_b.y, header_b.w, header_b.h, ImgID.UI_ResultBottomHeader );
    this.addChild( header );

    const title_w_design = 640;
    const title_b = this.box( ( DESIGN_W - title_w_design ) / 2, header_y_design + 54, title_w_design, 150 );

    const title = new UITextElement(
      this.ctx,
      title_b.x,
      title_b.y,
      title_b.w,
      title_b.h,
      {
        localization_key: "RESULT.UNLOCK_GOAL",
        font_size: LayoutScaler.textSizeFromDesign( 38 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        ...this.resultShadow(),
      }
    );
    this.addChild( title );

    const progress_bg_h = LayoutScaler.heightFromDesign( 79 );
    const scaled_fill_h = Math.min(
      LayoutScaler.heightFromDesign( 70 ),
      progress_bg_h
    );
    const fill_h = scaled_fill_h - ( ( progress_bg_h - scaled_fill_h ) & 1 );
    const fill_y = ( progress_bg_h - fill_h ) / 2;
    const progress_bg_design_w = 730;
    const progress_fill_inset_x_design = 6;
    const progress_fill_design_w = progress_bg_design_w - progress_fill_inset_x_design * 2;
    const progress_bg_x = LayoutScaler.xFromDesign( 60 );
    const progress_bg_w = LayoutScaler.widthFromDesign( progress_bg_design_w );

    const progress_bg = new UINineSliceElement(
      this.ctx,
      progress_bg_x,
      LayoutScaler.yFromDesign( 122 ),
      progress_bg_w,
      progress_bg_h,
      ImgID.UI_ResultProgressBarBg,
      "result_pb_bg",
      undefined,
      undefined,
      false,
      true
    );
    panel.addChild( progress_bg, true );

    const fill_max_w = LayoutScaler.widthFromDesign( progress_fill_design_w );
    const fill = new UINineSliceElement(
      this.ctx,
      LayoutScaler.xFromDesign( progress_fill_inset_x_design ),
      fill_y,
      is_available
        ? fill_max_w
        : Math.max( LayoutScaler.widthFromDesign( 70 ), fill_max_w * progress / target ),
      fill_h,
      is_available ? ImgID.UI_ResultProgressBarFillAvailable : ImgID.UI_ResultProgressBarFill,
      is_available ? "result_pb_fill_available" : "result_pb_fill",
      undefined,
      undefined,
      false,
      is_available
    );
    progress_bg.addChild( fill, true );

    const progress_text_w = LayoutScaler.widthFromDesign( is_available ? 450 : 190 );
    const progress_text_x = this.rect.getW2()
      - panel_b.x
      - progress_bg_x
      - progress_text_w / 2;
    const progress_text = new UITextElement(
      this.ctx,
      progress_text_x,
      LayoutScaler.yFromDesign( is_available ? 6 : 0 ),
      progress_text_w,
      progress_bg_h,
      {
        text: is_available ? undefined : `${progress}/${target}`,
        localization_key: is_available ? "RESULT.AVAILABLE" : undefined,
        font_size: LayoutScaler.textSizeFromDesign( is_available ? 42 : 48 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        scale_to_width: false,
        auto_wrap: false,
        ...this.resultShadow(),
      }
    );
    progress_bg.addChild( progress_text, true );

    // The block the bar is counting toward, not a generic prize box — same
    // table the hand-over overlay reads, so the two cannot disagree.
    const goal_icon = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 770 ),
      LayoutScaler.yFromDesign( 40 ),
      LayoutScaler.widthFromDesign( 211 ),
      LayoutScaler.heightFromDesign( 211 ),
      goalIconFor( span.unlocks )
    );
    panel.addChild( goal_icon, true );
  }

  private async collectGetReward( button: UIImage ): Promise<void>
  {
    if ( this.reward_inflight || this.continued ) return;

    void hapticsController.vibrate( HapticPattern.LIGHT, VibrationContext.GENERAL );
    this.reward_inflight = true;
    button.setClickable( false );
    try
    {
      const params = this.params as GameResultOverlayParams;
      const completed_level = params.rewarded_level ?? params.level;
      await tryShowInterstitial( "level_win", completed_level );
    }
    catch ( e )
    {
      console.warn( "[GameResultOverlay] Interstitial ad error:", e );
    }
    finally
    {
      this.reward_inflight = false;
      button.setClickable( true );
    }

    this.continueAfterReward();
  }

  private getCoinRewardPositions(): Vector2[]
  {
    const params = this.params as GameResultOverlayParams;
    const rewards = params?.rewards ?? [];
    if ( rewards.length === 0 ) return [];

    const ICON_D = GameResultOverlay.REWARD_ICON_W_DESIGN;
    const icon_dy = GameResultOverlay.REWARD_ICON_CY_DESIGN - ICON_D / 2;

    const positions: Vector2[] = [];
    const overlay_absolute = this.getAbsolutePosition();

    rewards.forEach( ( reward, i ) =>
    {
      if ( reward.currency === "coin" )
      {
        const center_dx = this.levelRewardCenterX( i, rewards.length );
        const icon_b = this.box( center_dx - ICON_D / 2, icon_dy, ICON_D, ICON_D );
        positions.push( new Vector2(
          overlay_absolute.x + icon_b.x + icon_b.w / 2,
          overlay_absolute.y + icon_b.y + icon_b.h / 2
        ) );
      }
    } );

    return positions;
  }

  private getCoinRewardAmount(): number
  {
    const params = this.params as GameResultOverlayParams;
    const rewards = params?.rewards ?? [];
    let total = 0;
    for ( const reward of rewards )
    {
      if ( reward.currency === "coin" )
        total += reward.amount;
    }
    return total;
  }

  // === [TEMP WORKAROUND: COIN FLY OVERLAY CLOSE DELAY] ===
  private continueAfterReward(): void
  {
    if ( this.continued ) return;
    this.continued = true;
    const params = this.params as GameResultOverlayParams;

    const completed_level = params.rewarded_level ?? params.level;
    if ( ( params.stars ?? 0 ) > 0 && completed_level !== undefined && completed_level > 0 && GameResultOverlay.goalJustEarned( completed_level ) )
      StatisticsStorage.update( { new_goal_pending: true } );

    const coin_positions = this.getCoinRewardPositions();
    const coin_reward_amount = this.getCoinRewardAmount();

    if ( coin_reward_amount <= 0 || coin_positions.length === 0 )
    {
      this.finishRewardSequence();
      return;
    }

    this.createCoinBadge();
    const badge_animation_start = performance.now();
    const coin_fly_start = badge_animation_start + GameResultOverlay.COIN_FLY_DELAY_MS;
    const icon_w = LayoutScaler.heightFromDesign( CurrencyBar.COIN_ICON_SIZE );
    const icon_h = icon_w;
    this.coin_icon_controller = Vfx2DManager.addCoinIcon(
      this.coin_badge_target,
      new Vector2( icon_w, icon_h ),
      badge_animation_start
    );

    if ( this.coin_badge )
    {
      this.coin_badge.setOpacity( 0 );
      this.coin_badge.setScale( new Vector2( 0.6, 0.6 ) );
      this.coin_badge.addTransform(
        new TransformOpacity(
          this.coin_badge,
          true,
          0,
          1,
          badge_animation_start,
          GameResultOverlay.COIN_BADGE_POP_MS,
          { easing: Easing.EaseOutQuad }
        )
      );
      this.coin_badge.addTransform(
        new TransformScale(
          this.coin_badge,
          true,
          null,
          new Vector2( 1.12, 1.12 ),
          badge_animation_start,
          GameResultOverlay.COIN_BADGE_POP_MS,
          { easing: Easing.EaseOutQuad }
        )
      );
      this.coin_badge.addTransform(
        new TransformScale(
          this.coin_badge,
          true,
          null,
          new Vector2( 1, 1 ),
          badge_animation_start + GameResultOverlay.COIN_BADGE_POP_MS,
          GameResultOverlay.COIN_BADGE_SETTLE_MS,
          { easing: Easing.EaseInOutQuad }
        )
      );
    }
    this.forceMakeShouldBeRendered();

    let coin_fly_finish_at = 0;
    for ( const pos of coin_positions )
      coin_fly_finish_at = Math.max(
        coin_fly_finish_at,
        Vfx2DManager.addCoinsFly( pos, this.coin_badge_target, 10, coin_fly_start )
      );

    this.coin_icon_controller.fadeOutAt( coin_fly_finish_at, COIN_TRANSITION_BADGE_FADE_MS );

    this.keep_coin_flight_on_close = true;
    // Stay long enough for the coins to visibly leave the reward icon on this
    // screen; the rest of the flight carries on over the next one.
    this.reward_finish_at = badge_animation_start + GameResultOverlay.COIN_OVERLAY_HOLD_MS;
  }

  private finishRewardSequence(): void
  {
    this.reward_finish_at = 0;
    const params = this.params as GameResultOverlayParams;
    try
    {
      params?.onContinue?.();
    }
    catch ( error )
    {
      console.error( "[GameResultOverlay] Continue failed:", error );
    }
    finally
    {
      OverlayManager.closeOverlay();
    }
  }

  private static currencyImage( currency: ResultRewardCurrency ): ImgID
  {
    return ADVENTURE_REWARD_ICONS[ currency ];
  }

  private startAppearAnimation(): void
  {
    if ( OverlayManager.useCssAnimations() )
    {
      OverlayManager.animateAppearCss( this, 0.85, GameResultOverlay.APPEAR_MS );
      return;
    }

    const now = performance.now();
    this.setScale( new Vector2( 0.85, 0.85 ) );
    this.setOpacity( 0 );
    this.addTransform( new TransformScale( this, true, null, new Vector2( 1, 1 ), now, GameResultOverlay.APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
    this.addTransform( new TransformOpacity( this, true, 0, 1, now, GameResultOverlay.APPEAR_MS, { easing: Easing.EaseOutQuad } ) );
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    if ( now < this.interactive_at || this.continued || this.reward_inflight )
      return true;
    return super.onTouchStart( p, now );
  }

  public update( now: number ): void
  {
    super.update( now );

    if ( this.coin_badge && this.coin_icon_controller )
    {
      this.coin_icon_controller.setOpacity( this.coin_badge.getOpacity() );
      const badge_scale = this.coin_badge.getScale();
      this.coin_icon_controller.setScale( badge_scale );

      const badge_absolute = this.coin_badge.getAbsolutePosition();
      const badge_rect = this.coin_badge.getRect();
      const badge_center = new Vector2(
        badge_absolute.x + badge_rect.getW() / 2,
        badge_absolute.y + badge_rect.getH() / 2
      );
      this.coin_icon_controller.setPosition( new Vector2(
        badge_center.x + ( this.coin_badge_target.x - badge_center.x ) * badge_scale.x,
        badge_center.y + ( this.coin_badge_target.y - badge_center.y ) * badge_scale.y
      ) );
    }

    if ( this.reward_finish_at !== 0 && now >= this.reward_finish_at )
      this.finishRewardSequence();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }

  public override onOverlayWillClose(): void
  {
    this.is_closed = true;
    this.stopFireworks();
    Vfx2DManager.clearConfetti();

    if ( this.coin_icon_controller )
    {
      if ( !this.keep_coin_flight_on_close )
        this.coin_icon_controller.stop();
      this.coin_icon_controller = null;
    }
    this.reward_glow_controller?.stop();
    this.reward_glow_controller = null;
    if ( !this.keep_coin_flight_on_close )
    {
      WebGLUIRuntime.clearAll()
      Vfx2DRuntime.clearAll();
    }
  }

  public name(): string
  {
    return "GameResultOverlay";
  }
}
